/**
 * The scope of work a deck customer reads, and what to say before generating.
 *
 * Jonathon, 2026-10-11: "use the scope of work on julia drabuik but change the
 * verbiage in the railing to pressure treated spindles." Julia Drabuik's deck
 * was a replacement, so her scope opens by tearing the old deck down; asked
 * whether every deck is one, he said to ask ("replacing a deck?"). So the
 * tear-down is in it only when the rep ticks that, and the square footage is
 * the deck's own rather than her 365.
 *
 * Written from the answers, the way a flat roof's scope is, because the
 * sections differ with them. Words below are hers except where the answers
 * change them: the new-build overview, the joist spacing on a Trex deck, and
 * Trex's boards.
 */

import {
  deckSquareFeet,
  defaultRailingLf,
  isTrexDeckType,
  joistSpacingFor,
  needsGuardRail,
  railingLfOf,
  stairSet,
  type DeckAnswers,
} from "./deck.js";

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** The longest board or post stocked. */
const LONGEST_FT = 16;
/** Below this, a deck ticked second story was likely left at the default height. */
const SECOND_STORY_FROM_FT = 8;

/** The numbered sections, in order, each a heading and its lines. */
function sections(
  answers: DeckAnswers,
  roofType: string | null | undefined,
): [string, string[]][] {
  const trex = isTrexDeckType(roofType);
  const spacing = joistSpacingFor(roofType);
  // Only what is ordered is promised: a freestanding deck has no ledger or
  // house flashing, and a deck with no steps or railing gets neither section.
  const stairs =
    Math.round(Number(answers.stairSets) || 0) > 0 &&
    stairSet(answers) !== null;
  // No railing means no handrails on the steps either (Jonathon, 2026-10-13).
  const railing =
    railingLfOf(answers) > 0 || (stairs && answers.railingRequired);
  const out: [string, string[]][] = [];
  if (answers.replacing) {
    out.push([
      "Demolition (Tear-down)",
      [
        "Take apart and remove all old boards, framing, railings, and stairs (if applicable).",
        "Haul away all old materials.",
      ],
    ]);
  }
  out.push([
    "Footings (Supports in the ground)",
    // Her words when an old deck is replaced (Jonathon, 2026-10-11: "i
    // prefer julia's verbiage on the footings."). A new deck has no footings
    // to check, so it digs new ones (Jonathon, 2026-10-12: "yes").
    answers.replacing
      ? [
          "Check all the current footings.",
          "Replace any that are bad or don’t meet building rules.",
        ]
      : ["Dig and pour new concrete footings that meet building rules."],
  ]);
  out.push([
    "Framing (The deck’s frame)",
    [
      "Posts: Use strong 6×6 pressure-treated wood posts.",
      ...(answers.attached
        ? [
            "Ledger board: Use 2×10 or larger pressure-treated boards with proper flashing.",
          ]
        : []),
      `Joists: Use 2×8 or larger boards, spaced ${spacing} inches apart unless specified otherwise.`,
      "Beams: Use 2×10 or larger boards.",
      "Use the right hardware and fasteners so everything is safe and secure.",
    ],
  ]);
  out.push([
    "Decking (Walking surface)",
    trex
      ? [
          "Secure down Trex composite deck boards.",
          "Fasten them with the fasteners Trex specifies.",
        ]
      : [
          "Secure down 5/4×6 pressure-treated boards.",
          "Fasten them with screws rated for exterior use.",
        ],
  ]);
  if (railing) {
    out.push([
      "Railing System",
      [
        "Top rail made from 2×6 boards.",
        "Bottom and middle rails made from 2×4 boards.",
        "Vertical pressure-treated spindles spaced per code to keep everyone safe.",
      ],
    ]);
  }
  if (stairs) {
    out.push([
      "Stairs",
      [
        "Build new stairs with 2×10 boards or larger for the stringers.",
        // Treads only: no riser boards are ordered. Jonathon, 2026-10-12:
        // "remove risers from future scopes".
        "Install treads that meet building codes.",
      ],
    ]);
  }
  if (answers.attached) {
    out.push([
      "Flashing",
      ["Put new flashing between the deck and the house to keep water out."],
    ]);
  }
  return out;
}

/** What the scope leaves out. The siding line speaks of the ledger, so only a
 * deck fastened to the house has it. Jonathon, 2026-10-12: "take it out". */
const notIncluded = (answers: DeckAnswers): string[] => [
  "Electrical work, plumbing, staining, painting, or sealing the deck.",
  "Replacing rotten wood under the house unless we find it later (this would be extra work and cost).",
  ...(answers.attached
    ? [
        "Replacing siding unless needed for the new ledger (extra work and cost if needed)",
      ]
    : []),
];

const DISCLAIMERS = [
  "Size is approximate and may vary by a few square feet.",
  "We may use other materials if the exact ones are not in stock, but they will be equal or greater in value.",
  "All work will follow building codes and manufacturer guidelines.",
];

/** The whole scope of work for this deck. */
export function deckScopeOfWork(
  answers: DeckAnswers,
  roofType: string | null | undefined,
): string {
  const wood = isTrexDeckType(roofType)
    ? "a new deck using Trex composite boards on a pressure-treated frame"
    : "a new wood deck using pressure-treated lumber";
  const overview = `${
    answers.replacing
      ? `We will tear down and throw away the old deck. Then we will build ${wood}.`
      : `We will build ${wood}.`
  } The deck will be about ${deckSquareFeet(answers)} square feet and will follow all building rules.`;

  const numbered = sections(answers, roofType);
  const body = numbered.map(
    ([heading, lines], at) => `${at + 1}. ${heading}\n${lines.join("\n")}`,
  );
  const left = `${numbered.length + 1}. Not Included:\n${notIncluded(answers).join("\n")}`;

  return [
    `Project Overview:\n${overview}`,
    `What’s Included:\n${body.join("\n\n")}`,
    `${left}\nDisclaimers:\n${DISCLAIMERS.join("\n")}`,
  ].join("\n\n");
}

/**
 * The scope written onto the estimate: the deck's own, and under it anything
 * the template's scope adds (it starts empty; the office can fill it in Tools).
 */
export function deckScopeNote(
  templateScope: string | null | undefined,
  answers: DeckAnswers,
  roofType: string | null | undefined,
): string {
  const own = (templateScope ?? "").trim();
  return [deckScopeOfWork(answers, roofType), own]
    .filter((part) => part !== "")
    .join("\n\n");
}

/* -------------------------------------------------------------------------
 * What to say first
 * ---------------------------------------------------------------------- */

/** What is missing or unusual, said before the estimate is generated. */
export function deckWarnings(answers: DeckAnswers): string[] {
  if (deckSquareFeet(answers) <= 0) {
    return [
      "The deck has no width or depth, so nothing is ordered and the crew is paid nothing.",
    ];
  }
  const warnings: string[] = [];
  if (num(answers.heightFt) > LONGEST_FT) {
    warnings.push(
      "The deck is more than 16 ft up. No post that long is stocked — price a two-storey deck with stacked posts by hand.",
    );
  }
  if (answers.secondStory && num(answers.heightFt) < SECOND_STORY_FROM_FT) {
    warnings.push(
      `The deck is ticked second story but is only ${num(answers.heightFt)} ft up. Type its real height: the posts, steps and railing are sized from it.`,
    );
  }
  const stairs =
    Math.round(num(answers.stairSets)) > 0 ? stairSet(answers) : null;
  if (stairs && stairs.stringerFt > LONGEST_FT) {
    warnings.push(
      "The steps run more than 16 ft, which needs a landing. Check the stringers by hand.",
    );
  }
  const shortRail =
    answers.railingLf !== null &&
    num(answers.railingLf) < defaultRailingLf(answers);
  if (needsGuardRail(answers) && !answers.railingRequired) {
    warnings.push(
      "The deck is 30 inches or more off the ground, and railing is not ticked. Code wants a guard rail on every open side.",
    );
  } else if (needsGuardRail(answers) && shortRail) {
    warnings.push(
      "The deck is 30 inches or more off the ground, and the railing is shorter than its open sides. Code wants a guard rail on every open side.",
    );
  }
  return warnings;
}
